import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Award, Plus, ToggleLeft, ToggleRight, Pencil, Trash2, ChevronRight } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LoyaltyProgram {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  settings: { description?: string; pointsPerUnit?: number; currency?: string } | null;
  createdAt: string;
}

interface ProgramForm {
  name: string;
  slug: string;
  description: string;
  pointsPerUnit: string;
  currency: string;
}

const EMPTY_FORM: ProgramForm = {
  name: '',
  slug: '',
  description: '',
  pointsPerUnit: '1',
  currency: 'EUR',
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function LoyaltyProgramsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  // Auto-redirect removed: with topbar tabs, the programs list is always
  // directly accessible via the "Programs" tab. Users enter a specific
  // program by clicking it in the list or using the ProgramSwitcher.

  const [showCreate, setShowCreate] = useState(false);
  const [editProgram, setEditProgram] = useState<LoyaltyProgram | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<LoyaltyProgram | null>(null);
  const [form, setForm] = useState<ProgramForm>(EMPTY_FORM);
  const [showInactive, setShowInactive] = useState(false);

  const { data = [], isLoading } = useQuery<LoyaltyProgram[]>({
    queryKey: ['loyalty-programs', showInactive],
    queryFn: () =>
      api
        .get('/api/v1/loyalty/programs', {
          params: { includeInactive: showInactive ? 'true' : undefined },
        })
        .then((r) => r.data),
  });

  // ── mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post('/api/v1/loyalty/programs', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-programs'] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      toast.success('Program created');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create program')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: object }) =>
      api.patch(`/api/v1/loyalty/programs/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-programs'] });
      setEditProgram(null);
      toast.success('Program updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update program')),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/api/v1/loyalty/programs/${id}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-programs'] });
      setDeactivateTarget(null);
      toast.success('Program status updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── helpers ──────────────────────────────────────────────────────────────────

  function openCreate() {
    setForm(EMPTY_FORM);
    setShowCreate(true);
  }

  function openEdit(p: LoyaltyProgram) {
    const s = p.settings ?? {};
    setForm({
      name: p.name,
      slug: p.slug,
      description: s.description ?? '',
      pointsPerUnit: String(s.pointsPerUnit ?? 1),
      currency: s.currency ?? 'EUR',
    });
    setEditProgram(p);
  }

  function handleCreate() {
    if (!form.name || !form.slug) return;
    createMutation.mutate({
      name: form.name,
      slug: form.slug,
      description: form.description || undefined,
      pointsPerUnit: parseFloat(form.pointsPerUnit) || 1,
      currency: form.currency || 'EUR',
    });
  }

  function handleUpdate() {
    if (!editProgram) return;
    updateMutation.mutate({
      id: editProgram.id,
      body: {
        name: form.name,
        description: form.description || undefined,
        pointsPerUnit: parseFloat(form.pointsPerUnit) || 1,
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Loyalty Programs"
        description="Manage point accumulation programs and their configurations"
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowInactive((v) => !v)}
              className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
            >
              {showInactive ? 'Hide inactive' : 'Show inactive'}
            </button>
            <Button onClick={openCreate} size="sm">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Program
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={6} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={<Award size={26} />}
          title="No loyalty programs"
          description="Create your first program to start tracking points and tiers"
          action={{ label: 'New Program', onClick: openCreate }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Program
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Pts / Unit
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Currency
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 w-32" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-zinc-50 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/loyalty/${p.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{p.name}</div>
                    {p.settings?.description && (
                      <div className="text-xs text-zinc-400 mt-0.5 truncate max-w-xs">
                        {p.settings.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{p.slug}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-700">
                    {p.settings?.pointsPerUnit ?? 1}
                  </td>
                  <td className="px-4 py-3 text-zinc-500">{p.settings?.currency ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant={p.isActive ? 'success' : 'default'}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(p);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        title="Edit"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (p.isActive) {
                            setDeactivateTarget(p);
                          } else {
                            toggleMutation.mutate({ id: p.id, isActive: true });
                          }
                        }}
                        className="text-zinc-400 hover:text-zinc-700 transition-colors"
                        title={p.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {p.isActive ? (
                          <ToggleRight className="w-5 h-5 text-zinc-700" />
                        ) : (
                          <ToggleLeft className="w-5 h-5" />
                        )}
                      </button>
                      <ChevronRight size={13} className="text-zinc-300" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setForm(EMPTY_FORM);
        }}
        title="New Loyalty Program"
      >
        <ProgramFormFields form={form} setForm={setForm} lockSlug={false} />
        {createMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(createMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            loading={createMutation.isPending}
            disabled={!form.name || !form.slug}
            onClick={handleCreate}
          >
            Create Program
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editProgram}
        onClose={() => setEditProgram(null)}
        title={`Edit: ${editProgram?.name}`}
      >
        <ProgramFormFields form={form} setForm={setForm} lockSlug />
        {updateMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(updateMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setEditProgram(null)}>
            Cancel
          </Button>
          <Button loading={updateMutation.isPending} disabled={!form.name} onClick={handleUpdate}>
            Save changes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Deactivate confirm */}
      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate program?"
        message={`"${deactivateTarget?.name}" will be deactivated. Members retain their points and tier.`}
        confirmLabel="Deactivate"
        variant="warning"
        loading={toggleMutation.isPending}
        onConfirm={() =>
          deactivateTarget && toggleMutation.mutate({ id: deactivateTarget.id, isActive: false })
        }
        onCancel={() => setDeactivateTarget(null)}
      />
    </div>
  );
}

// ─── Form fields subcomponent ────────────────────────────────────────────────

function ProgramFormFields({
  form,
  setForm,
  lockSlug,
}: {
  form: ProgramForm;
  setForm: React.Dispatch<React.SetStateAction<ProgramForm>>;
  lockSlug: boolean;
}) {
  return (
    <div className="space-y-4">
      <Input
        label="Program name *"
        value={form.name}
        onChange={(e) => {
          const name = e.target.value;
          setForm((f) => ({ ...f, name, ...(lockSlug ? {} : { slug: slugify(name) }) }));
        }}
        placeholder="Standard Rewards"
      />
      <div>
        <Input
          label="Slug *"
          value={form.slug}
          onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
          placeholder="standard-rewards"
          className="font-mono text-xs"
          disabled={lockSlug}
        />
        {!lockSlug && (
          <p className="text-xs text-zinc-400 mt-1">
            Lowercase, hyphens only. Cannot be changed after creation.
          </p>
        )}
      </div>
      <Input
        label="Description"
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        placeholder="Optional"
      />
      <div className="flex gap-3">
        <div className="flex-1">
          <Input
            label="Points per unit"
            type="number"
            min="0"
            step="0.01"
            value={form.pointsPerUnit}
            onChange={(e) => setForm((f) => ({ ...f, pointsPerUnit: e.target.value }))}
          />
        </div>
        <div className="w-28">
          <Input
            label="Currency"
            value={form.currency}
            onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
            placeholder="EUR"
          />
        </div>
      </div>
    </div>
  );
}
